import type { Invoice } from '@monorepo/shared/schemas/invoices'
import { createFileRoute } from '@tanstack/react-router'
import { Home } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { invoicesService } from '@/features/invoices'
import { InvoiceDetailContent } from '@/features/invoices/components/InvoiceDetailContent'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

export const Route = createFileRoute('/_auth/invoice-detail/$invoiceId')({
  component: InvoiceDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    from: search.from as string | undefined,
  }),
})

function InvoiceDetailPage() {
  const { invoiceId } = Route.useParams()
  const { setBreadcrumbs } = useBreadcrumbs()
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadInvoice = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await invoicesService.getInvoice(invoiceId)
      setInvoice(response.data)
    } catch (err) {
      toast.error(extractErrorMessage(err))
      setError('Failed to load invoice')
    } finally {
      setLoading(false)
    }
  }, [invoiceId])

  useEffect(() => {
    loadInvoice()
  }, [loadInvoice])

  // Breadcrumbs setup
  const breadcrumbsContent = useMemo(() => {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to='/invoices'>Invoices</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              {invoice?.invoice_number || invoiceId}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }, [invoice, invoiceId])

  useEffect(() => {
    if (breadcrumbsContent) {
      setBreadcrumbs(breadcrumbsContent)
    }
  }, [setBreadcrumbs, breadcrumbsContent])

  if (loading) {
    return (
      <div className='container mx-auto py-6'>
        <div className='animate-pulse space-y-6'>
          <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-1/3' />
          <div className='grid grid-cols-1 lg:grid-cols-5 gap-6'>
            <div className='lg:col-span-3 space-y-6'>
              <div className='h-48 bg-slate-300 dark:bg-slate-600 rounded' />
              <div className='h-64 bg-slate-300 dark:bg-slate-600 rounded' />
            </div>
            <div className='lg:col-span-2 space-y-6'>
              <div className='h-32 bg-slate-300 dark:bg-slate-600 rounded' />
              <div className='h-24 bg-slate-300 dark:bg-slate-600 rounded' />
              <div className='h-32 bg-slate-300 dark:bg-slate-600 rounded' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className='container mx-auto py-6'>
        <div className='text-center py-12'>
          <div className='text-red-600 mb-2'>
            {error || 'Invoice not found'}
          </div>
          <a href='/invoices' className='text-sm text-blue-600 hover:underline'>
            Back to Invoices
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className='container mx-auto py-6'>
      <InvoiceDetailContent invoice={invoice} onInvoiceUpdated={loadInvoice} />
    </div>
  )
}
